/**
 * React Context for UIC activities from microservice
 * Provides all activities/themes with inherited colours throughout the app
 * Activities are fetched server-side via microservice /api/v1/activities
 */

'use client';

import type { Country, ThemeNode } from '@/types/microservices';
import { createContext, useContext, useMemo } from 'react';

interface ActivitiesContextValue {
  tree: ThemeNode[];
  countries: Country[];
  getActivityByName: (name: string) => ThemeNode | undefined;
  getActivityById: (id: string) => ThemeNode | undefined;
  getActivityColor: (name: string) => string | null | undefined;
  getActivitiesByType: (type: string) => ThemeNode[];
  getCountryByName: (name: string) => Country | undefined;
  getCountryByIso2: (iso2: string) => Country | undefined;
  getCountryByIso3: (iso3: string) => Country | undefined;
}

const ActivitiesContext = createContext<ActivitiesContextValue | null>(null);

interface ActivitiesProviderProps {
  tree: ThemeNode[];
  countries: Country[];
  children: React.ReactNode;
}

/**
 * Flatten tree to array for lookup functions
 */
function flattenTree(nodes: ThemeNode[]): ThemeNode[] {
  const result: ThemeNode[] = [];
  function traverse(node: ThemeNode) {
    result.push(node);
    node.children.forEach(traverse);
  }
  nodes.forEach(traverse);
  return result;
}

/**
 * Provider for UIC activities and countries from microservices
 * Should be placed high in the component tree (e.g., in layout)
 *
 * @example
 * ```tsx
 * // In layout (Server Component)
 * import { getActivities, getCountries, buildThemeTree } from '@/lib/microservices/server-client';
 *
 * const activities = await getActivities();
 * const countries = await getCountries();
 * const tree = buildThemeTree(activities);
 *
 * return (
 *   <ActivitiesProvider tree={tree} countries={countries}>
 *     {children}
 *   </ActivitiesProvider>
 * );
 * ```
 */
export function ActivitiesProvider({ tree, countries, children }: ActivitiesProviderProps) {
  const value = useMemo(() => {
    // Flatten tree for O(1) lookup
    const flatActivities = flattenTree(tree);
    const activitiesMapByName = new Map(flatActivities.map(a => [a.name, a]));
    const activitiesMapById = new Map(flatActivities.map(a => [a.id, a]));

    // Ensure countries is always an array (defensive check)
    const countriesList = Array.isArray(countries) ? countries : [];

    // Create maps for countries lookup
    const countriesMapByName = new Map(countriesList.map(c => [c.name, c]));
    const countriesMapByIso2 = new Map(countriesList.map(c => [c.iso2, c]));
    const countriesMapByIso3 = new Map(countriesList.map(c => [c.iso3, c]));

    return {
      tree,
      countries: countriesList,
      getActivityByName: (name: string) => activitiesMapByName.get(name),
      getActivityById: (id: string) => activitiesMapById.get(id),
      getActivityColor: (name: string) => activitiesMapByName.get(name)?.color || null,
      getActivitiesByType: (type: string) => flatActivities.filter(a => a.type === type),
      getCountryByName: (name: string) => countriesMapByName.get(name),
      getCountryByIso2: (iso2: string) => countriesMapByIso2.get(iso2),
      getCountryByIso3: (iso3: string) => countriesMapByIso3.get(iso3),
    };
  }, [tree, countries]);

  return <ActivitiesContext.Provider value={value}>{children}</ActivitiesContext.Provider>;
}

/**
 * Hook to access UIC activities context
 *
 * @throws Error if used outside of ActivitiesProvider
 *
 * @example
 * ```tsx
 * const { tree, getActivityColor } = useActivities();
 *
 * const color = getActivityColor('Railway System');
 * ```
 */
export function useActivities() {
  const context = useContext(ActivitiesContext);

  if (!context) {
    throw new Error('useActivities must be used within an ActivitiesProvider');
  }

  return context;
}

